import * as AuthCustomer from './customer/AuthCustomer.selectors';
import * as AuthUser from './user/AuthUser.selectors';

import { AUTH_STATES } from '../constants';
import { DOMAIN } from './Auth.constants';
import get from 'lodash/get';
import { getAuthState as getAmplifyAuthState } from './amplify/Amplify.selectors';

export const devMode = ['development', 'dev', 'ci'].includes(process.env.MODE);

export const customer = AuthCustomer;
export const user = AuthUser;

export const getStore = state => get(state, DOMAIN) || {};

export const getCurrentAuthState = state => getStore(state).authState;
export const getAuthState = state => {
    const authState = getCurrentAuthState(state);
    if (authState === AUTH_STATES.signedIn) {
        return getAmplifyAuthState(state);
    }
    return authState;
};

export const getError = state => getStore(state).error;
export const getPhone = state => user.getPhone(state) || '';
export const getCustomer = state => customer.getStore(state);
export const getCustomerId = state => customer.getId(state);
export const getCustomerToken = state => getStore(state).customerToken;
export const getEmail = state => `${user.getEmail(state) || ''}`.toLowerCase();
export const getMfaType = state => getStore(state).mfaType;
export const getMfaOptions = state => getStore(state).mfaOptions || [];
export const getSessionToken = state => getStore(state).sessionToken || '';
export const getSignUpCodeResent = state => getStore(state).resentCode;
export const getPermissionsDetermined = state =>
    (customer.getRoles(state).length > 0 && user.getRoles(state).length > 0) || false;

export const isAutomatedTestingUser = state => {
    /**
     * To assist with automated testing, if the user is using an automated testing
     * osano.com email, we don't require MFA setup.
     */
    const email = getEmail(state);
    return email.startsWith('automatedtest') && email.endsWith('@osano.com');
};
export const isAuthenticated = state => {
    const authState = getCurrentAuthState(state);
    switch (authState) {
        case AUTH_STATES.signedIn: {
            // Being authenticated is slightly more complicated than just checking the auth state
            const amplifyAuthState = getAmplifyAuthState(state);
            return amplifyAuthState !== AUTH_STATES.signIn;
        }
        default:
            return false;
    }
};
export const isCustomerSelected = state => getCustomerToken(state) !== null;
export const needsAccountConfirmation = state => getAuthState(state) === AUTH_STATES.confirmSignUp;
export const isEmailVerified = state => user.isEmailVerified(state) || false;
export const isPhoneVerified = state => user.isPhoneVerified(state) || false;
export const isSsoEnabled = state => getStore(state).ssoEnabled;

export const needsTOTPSetup = state => getAuthState(state) === AUTH_STATES.setupTOTP;
export const needsEmailVerification = state => getAuthState(state) === AUTH_STATES.verifyEmail;
export const needsPhoneVerification = state => getAuthState(state) === AUTH_STATES.verifyPhone;
export const needsAccountVerififaction = state =>
    needsEmailVerification(state) || needsPhoneVerification(state);
export const needsPhoneSetup = state => getAuthState(state) === AUTH_STATES.setupPhone;
export const needsTOTPVerification = state => getAuthState(state) === AUTH_STATES.verifyTOTP;
export const needsPasswordRecovery = state => getAuthState(state) === AUTH_STATES.forgotPassword;
export const needsSignInConfirmed = state => getAuthState(state) === AUTH_STATES.confirmSignIn;
export const needsNewInvite = state => getAuthState(state) === AUTH_STATES.resendInvitation;
export const needsNewPassword = state => getAuthState(state) === AUTH_STATES.requireNewPassword;

export const needsMFASelected = state => getAuthState(state) === AUTH_STATES.selectMFA;
export const needsSignIn = state => getAuthState(state) === AUTH_STATES.signedOut;
export const signInConfirmed = state => getAuthState(state) === AUTH_STATES.signInConfirmed;
export const shouldRemember = state => Boolean(getStore(state).rememberMe);
export const shouldRefreshTokens = (
    state,
    /** @type {import('aws-amplify/auth').AuthSession} */ session
) => session?.tokens?.idToken?.toString() !== getSessionToken(state);

export const isLoading = state => getStore(state).loading;
export const isFetchingContext = state => getStore(state).fetchingContext;
export const isLoadingMfaOptions = state => getStore(state).fetchingMfaOptions;
